import { useState } from "react";

interface BookingFormProps {
  villas: any[];
  whatsappUrl: string;
}

export default function BookingForm({ villas, whatsappUrl }: BookingFormProps) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    checkin: "",
    checkout: "",
    guests: "2",
    villa: "",
  });
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    
    try {
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost/dashboard/New%20folder/patalkikian-nextjs/cms/index.php/wp-json/patalkikian/v1';
      const response = await fetch(`${apiBaseUrl}/bookings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          customer_name: `${formData.firstName} ${formData.lastName}`,
          customer_email: formData.email,
          customer_phone: formData.phone,
          checkin: formData.checkin,
          checkout: formData.checkout,
          guests: formData.guests,
          villa: formData.villa,
          total: "Pending Calculation",
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to submit booking");
      }

      setStatus("success");
      setMessage("Thank you! Your booking request has been received. Our concierge will contact you shortly to confirm your reservation and arrange payment.");
      setFormData({
        firstName: "", lastName: "", email: "", phone: "", checkin: "", checkout: "", guests: "2", villa: ""
      });
    } catch (error) {
      console.error(error);
      setStatus("error");
      setMessage("An error occurred while submitting your booking. Please try again or contact us via WhatsApp.");
    }
  };

  return (
    <>
      {status === "success" ? (
        <div className="bg-green-900/40 border border-green-500/50 p-8 rounded-xl text-center">
          <h3 className="text-2xl font-luxury text-gold mb-4">Request Received</h3>
          <p className="text-white/80 leading-relaxed mb-8">{message}</p>
          <button 
            onClick={() => setStatus("idle")}
            className="px-6 py-3 bg-transparent border border-white/20 text-white hover:border-gold hover:text-gold transition-colors uppercase tracking-widest text-xs cursor-pointer"
          >
            Submit Another Request
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          {status === "error" && (
            <div className="bg-red-900/40 border border-red-500/50 p-4 rounded text-white/90 text-sm text-center mb-2">
              {message}
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-2">
              <label className="text-sm text-white/70 uppercase tracking-wider">First Name *</label>
              <input required type="text" name="firstName" value={formData.firstName} onChange={handleChange} className="bg-transparent border border-white/20 p-3 text-white focus:outline-none focus:border-gold transition-colors" />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm text-white/70 uppercase tracking-wider">Last Name *</label>
              <input required type="text" name="lastName" value={formData.lastName} onChange={handleChange} className="bg-transparent border border-white/20 p-3 text-white focus:outline-none focus:border-gold transition-colors" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-2">
              <label className="text-sm text-white/70 uppercase tracking-wider">Email Address *</label>
              <input required type="email" name="email" value={formData.email} onChange={handleChange} className="bg-transparent border border-white/20 p-3 text-white focus:outline-none focus:border-gold transition-colors" />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm text-white/70 uppercase tracking-wider">Phone Number *</label>
              <input required type="tel" name="phone" value={formData.phone} onChange={handleChange} className="bg-transparent border border-white/20 p-3 text-white focus:outline-none focus:border-gold transition-colors" />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm text-white/70 uppercase tracking-wider">Select Room *</label>
            <select required name="villa" value={formData.villa} onChange={handleChange} className="bg-transparent border border-white/20 p-3 text-white focus:outline-none focus:border-gold transition-colors [&>option]:bg-secondary">
              <option value="" disabled>Select a Room</option>
              {villas.map((v) => (
                <option key={v.slug} value={v.title}>{v.title}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col gap-2">
              <label className="text-sm text-white/70 uppercase tracking-wider">Check-in *</label>
              <input required type="date" name="checkin" value={formData.checkin} onChange={handleChange} className="bg-transparent border border-white/20 p-3 text-white focus:outline-none focus:border-gold transition-colors" />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm text-white/70 uppercase tracking-wider">Check-out *</label>
              <input required type="date" name="checkout" value={formData.checkout} onChange={handleChange} className="bg-transparent border border-white/20 p-3 text-white focus:outline-none focus:border-gold transition-colors" />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm text-white/70 uppercase tracking-wider">Guests *</label>
              <input required type="number" min="1" max="10" name="guests" value={formData.guests} onChange={handleChange} className="bg-transparent border border-white/20 p-3 text-white focus:outline-none focus:border-gold transition-colors" />
            </div>
          </div>

          <button 
            type="submit" 
            disabled={status === "loading"}
            className="mt-4 w-full py-4 bg-gold text-black hover:bg-white hover:text-black transition-colors uppercase tracking-widest text-sm font-semibold disabled:opacity-50 cursor-pointer"
          >
            {status === "loading" ? "Submitting..." : "Submit Reservation Request"}
          </button>
        </form>
      )}
      
      <div className="mt-12 text-center border-t border-white/10 pt-8">
        <p className="text-white/70 mb-4">Or contact us directly via WhatsApp for faster response:</p>
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center px-8 py-4 bg-green-600 text-white hover:bg-green-700 transition-colors uppercase tracking-widest text-sm font-semibold"
        >
          Chat on WhatsApp
        </a>
      </div>
    </>
  );
}
